<template>
  <v-container class="custom-select pa-0 mt-1" :class="{ 'custom-select--chips-code': chipsCodeDisplay }">
    <v-menu v-model="menu" :close-on-content-click="false" offset-y>
      <template v-slot:activator="{ on, attrs }">
        <div v-bind="chipsCodeDisplay ? {} : attrs" v-on="disabled || chipsCodeDisplay ? {} : on">
          <v-text-field
            :value="chip ? '' : selectedValues"
            :disabled="disabled"
            readonly
            :outlined="outlined"
            :loading="loading"
            hide-details
            dense
          >
            <template #prepend-inner>
              <div v-if="chip && multiple" class="d-flex align-center mb-1">
                <div
                  class="custom-select__chips"
                >
                  <v-chip
                    v-for="item in selectedValuesForChipDisplay"
                    :key="item.value"
                    deafault
                    outlined
                    :close="!disabled"
                    :color="chipColor"
                    class="custom-select__chip"
                    @click:close="event => removeSelectedItem(item, event)"
                  >
                    {{ chipLabel(item) }}
                  </v-chip>
                </div>
                <span
                  v-if="selectionOverCount > 0"
                  class="custom-select__selection-over-count ml-2"
                  :style="`background-color: ${chipColor};`"
                >
                +{{ selectionOverCount }}
                </span>
                <v-btn
                  v-if="chipsCodeDisplay"
                  icon
                  x-small
                  color="success"
                  class="custom-select__add-btn ml-1"
                  :disabled="disabled"
                  v-bind="attrs"
                  v-on="disabled ? {} : on"
                >
                  <v-icon>mdi-plus-circle-outline</v-icon>
                </v-btn>
              </div>
            </template>
            <template v-if="!chipsCodeDisplay" #append>
              <v-icon>{{menu ? 'mdi-menu-up' : 'mdi-menu-down'}}</v-icon>
            </template>
          </v-text-field>
        </div>
      </template>

      <v-card>
        <v-text-field
          v-model="search"
          label="Search"
          prepend-inner-icon="mdi-magnify"
          hide-details
          :autofocus="menu ? autofocus : false"
          class="ma-0 px-4"
        ></v-text-field>
        <v-virtual-scroll
          :items="filteredItems"
          :item-height="50"
          height="300"
        >
          <template v-slot:default="{ item }">
            <v-list-item
              @click="selectItem(item)"
              class="pr-1"
              :class="{ 'primary--text': isSelectedItemsForFormatting(item) }"
            >
              <slot name="item" :item="item">
                <v-list-item-content class="d-flex">
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                </v-list-item-content>
              </slot>

              <v-list-item-action v-if="isSelectedItemsForFormatting(item) && isShowSelectedItemIcon">
                <v-icon color="green" small>mdi-check</v-icon>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-virtual-scroll>
        <v-divider></v-divider>
        <v-card-actions
          v-if="showActions"
          class="d-flex
          justify-end"
        >
          <v-btn text @click="cancelSelection">Cancel</v-btn>
          <v-btn class="ml-0" color="green" text @click="applySelection">Apply</v-btn>
        </v-card-actions>
        <v-card-actions
          v-if="showClearAction"
          class="d-flex
          justify-end"
        >
          <v-btn text @click="clearChoice">Clear</v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </v-container>
</template>

<script>

  export default {
    name: 'CustomSelect',
    data () {
      return {
        menu: false,
        search: '',
        selectedItems: this.multiple ? [] : {},
        maxVisibleSelections: 4,
        maxVisibleChipsCodeSelections: 10,
      }
    },
    props: {
      items: {
        type: [ Object, Number, String, Array ],
        require: true
      },
      value: {
        type: [ Object, Number, String, Array ],
        require: true
      },
      autoSelectFirst: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      outlined: {
        type: Boolean,
        default: true
      },
      multiple: {
        type: Boolean,
        default: false
      },
      isShowSelectedItemIcon: {
        type: Boolean,
        default: true
      },
      autofocus: {
        type: Boolean,
        default: true
      },
      showClearAction: {
        type: Boolean,
        default: false
      },
      loading: {
        type: Boolean,
        default: false
      },
      chip: {
        type: Boolean,
        default: false
      },
      chipColor: {
        type: String,
        default: 'success'
      },
      chipsCodeDisplay: {
        type: Boolean,
        default: false
      }
    },
    watch: {
      items: {
        handler (items) {
          if (this.autoSelectFirst) {
            this.selectedItems = items[0]
          }
        },
        immediate: true,
        deep: true
      },
      foundValue: {
        handler () {
          this.selectedItems = this.foundValue
        },
        immediate: true,
        deep: true
      },
    },
    computed: {
      visibleSelectionsLimit () {
        return this.chipsCodeDisplay ? this.maxVisibleChipsCodeSelections : this.maxVisibleSelections
      },
      showActions () {
        return this.multiple ? true : false
      },
      filteredItems () {
        return this.items.filter((item) =>
          item.text.toLowerCase().includes(this.search.toLowerCase()) || item?.fullName?.toLowerCase()?.includes?.(this.search?.toLowerCase())
        )
      },
      foundValue () {
        return this.multiple
          ? this.items.filter(({ value }) => this.value.includes(value))
          : this.items.find(({ value }) => this.value === value)
      },
      selectedValues () {
        if (this.multiple) {
          const selected = this.selectedItems?.map(({ text }) => text)
          return selected.length ? selected : this.foundValue?.map(({ text }) => text)
        }
        return this.selectedItems?.text || this.foundValue?.text
      },
      selectedValuesForChipDisplay () {
        if (!this.multiple) {
          return this.selectedItems?.value ? [ this.selectedItems ] : (this.foundValue ? [ this.foundValue ] : [])
        }

        const selected = this.selectedItems?.length ? this.selectedItems : (this.foundValue || [])
        return selected.slice(0, this.visibleSelectionsLimit)
      },
      displayedSelectedValues () {
        if (!this.multiple) {
          return this.selectedValues
        }

        return (this.selectedValues || [])
          .slice(0, this.visibleSelectionsLimit)
          .join(', ')
      },
      selectionOverCount () {
        if (!this.multiple) {
          return 0
        }

        const selectedCount = (this.selectedValues || []).length
        return selectedCount > this.visibleSelectionsLimit
          ? selectedCount - this.visibleSelectionsLimit
          : 0
      },
    },
    methods: {
      chipLabel (item) {
        return this.chipsCodeDisplay ? item.value : item.fullName
      },
      isSelectedItemsForFormatting (item) {
        return this.multiple
          ? this.selectedItems?.some?.(({ value }) => item.value === value) || this.foundValue?.some?.(({ value }) => item.value === value)
          : this.selectedItems?.value === item.value || this.foundValue?.value === item.value
      },
      selectItem (item) {
        if (this.multiple) {
          const index = this.selectedItems.findIndex(
            (selectedItem) => selectedItem.value === item.value
          )
          index === -1
            ? this.selectedItems.push(item)
            : this.selectedItems.splice(index, 1)
        } else {
          this.selectedItems = item
          this.menu = !this.menu
        }
        this.$emit('input', this.selectedItems)
      },

      removeSelectedItem (item, event ) {
        event?.stopPropagation?.()

        if (!this.multiple) {
          return
        }

        this.selectedItems = this.selectedItems.filter(({ value }) => value !== item.value)
        this.$emit('input', this.selectedItems)
      },

      applySelection () {
        this.menu = false
      },
      clearChoice () {
        this.search = ''
        this.selectedItems = this.multiple ? [] : {}
        this.$emit('clear')
        this.menu = false
      },
      cancelSelection () {
        this.search = ''
        this.menu = false
      },
    },
  }
</script>

<style lang="scss" scoped>
  .custom-select {
    max-width: 200px;

    &::v-deep .v-input__prepend-inner {
      width: 90%;

      & > div:first-child {
        flex-wrap: nowrap;
        overflow: hidden;
      }

    }

    &--chips-code {
      &::v-deep .v-text-field > .v-input__control > .v-input__slot:before {
        border-style: none;
      }

      &::v-deep .primary--text{
        box-shadow: none !important;
        color: transparent !important;
        caret-color: transparent !important;
      }

      &::v-deep .v-input__slot:before, .v-input__slot:after {
        transition: none !important;
      }

      &::v-deep .v-input__slot:after {
        transition: none !important;
      }
    }



    &__selection-over-count {
      color: white;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      flex-shrink: 0;
    }

    &__chips {
      display: flex;
      flex-wrap: nowrap;
      overflow: hidden;
      gap: 6px;
    }

    &__chip {
      flex-shrink: 0;
    }

    &_input {
      &::v-deep input {
        min-width: 0;
      }
    }
  }
</style>
